import React from 'react';
import { ChevronDown } from 'lucide-react';

const Hero = () => {
  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#3eb5a0] via-[#55c4af] to-[#6dd4bf]">
      {/* Vertical Bar Chart Pattern - Wave Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute bottom-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 800">
          {Array.from({ length: 200 }).map((_, i) => {
            const x = (i * 1440) / 200;
            const waveHeight = Math.sin((i / 200) * Math.PI * 4) * 200 + 250;
            const opacity = 0.15 + Math.sin((i / 200) * Math.PI * 2) * 0.1;
            return (
              <rect
                key={i}
                x={x}
                y={800 - waveHeight}
                width="6"
                height={waveHeight}
                fill="#2dd4bf"
                opacity={opacity}
              />
            );
          })}
        </svg>
      </div>

      {/* High-Tech Network Graphic - Right Side - Enhanced */}
      <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[900px] h-[900px] opacity-35 hidden lg:block pointer-events-none">
        <svg viewBox="0 0 900 900" className="w-full h-full">
          {/* Large orbital rings - more prominent */}
          <circle cx="600" cy="450" r="350" fill="none" stroke="white" strokeWidth="2" opacity="0.6" className="animate-pulse" style={{ animationDuration: '4s' }} />
          <circle cx="600" cy="450" r="300" fill="none" stroke="white" strokeWidth="2" opacity="0.7" className="animate-pulse" style={{ animationDuration: '3.5s' }} />
          <circle cx="600" cy="450" r="250" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8" className="animate-pulse" style={{ animationDuration: '3s' }} />
          <circle cx="600" cy="450" r="200" fill="none" stroke="white" strokeWidth="1.5" opacity="0.6" className="animate-pulse" style={{ animationDuration: '3.8s' }} />
          
          {/* Communication tower structure - prominent */}
          <g className="animate-pulse" style={{ animationDuration: '5s' }}>
            {/* Main tower */}
            <line x1="600" y1="200" x2="600" y2="50" stroke="white" strokeWidth="4" opacity="0.9" />
            <line x1="600" y1="200" x2="570" y2="150" stroke="white" strokeWidth="3" opacity="0.8" />
            <line x1="600" y1="200" x2="630" y2="150" stroke="white" strokeWidth="3" opacity="0.8" />
            <line x1="570" y1="150" x2="540" y2="100" stroke="white" strokeWidth="2.5" opacity="0.7" />
            <line x1="630" y1="150" x2="660" y2="100" stroke="white" strokeWidth="2.5" opacity="0.7" />
            <line x1="540" y1="100" x2="510" y2="60" stroke="white" strokeWidth="2" opacity="0.6" />
            <line x1="660" y1="100" x2="690" y2="60" stroke="white" strokeWidth="2" opacity="0.6" />
            
            {/* Tower top beacon */}
            <circle cx="600" cy="50" r="12" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2s' }} />
            <circle cx="600" cy="50" r="6" fill="white" opacity="1" />
            
            {/* Signal waves from tower */}
            <path d="M 600 50 Q 650 100 700 100" fill="none" stroke="white" strokeWidth="1" opacity="0.5" strokeDasharray="3,3" className="animate-pulse" />
            <path d="M 600 50 Q 550 100 500 100" fill="none" stroke="white" strokeWidth="1" opacity="0.5" strokeDasharray="3,3" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
          </g>
          
          {/* Network nodes and connections - enhanced */}
          <g>
            {/* Major connection nodes */}
            <circle cx="600" cy="450" r="8" fill="white" opacity="1" />
            <circle cx="750" cy="300" r="7" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2.5s' }} />
            <circle cx="700" cy="600" r="7" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '3s' }} />
            <circle cx="450" cy="400" r="7" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2.8s' }} />
            <circle cx="500" cy="550" r="7" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '3.3s' }} />
            <circle cx="800" cy="450" r="6" fill="white" opacity="0.8" className="animate-ping" style={{ animationDuration: '2.6s' }} />
            
            {/* Secondary nodes */}
            <circle cx="650" cy="350" r="5" fill="white" opacity="0.7" />
            <circle cx="720" cy="480" r="5" fill="white" opacity="0.7" />
            <circle cx="550" cy="480" r="5" fill="white" opacity="0.7" />
            
            {/* Primary connection lines */}
            <line x1="600" y1="450" x2="750" y2="300" stroke="white" strokeWidth="2" opacity="0.7" strokeDasharray="5,5" className="animate-dash" />
            <line x1="600" y1="450" x2="700" y2="600" stroke="white" strokeWidth="2" opacity="0.7" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '0.5s' }} />
            <line x1="600" y1="450" x2="450" y2="400" stroke="white" strokeWidth="2" opacity="0.7" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '1s' }} />
            <line x1="600" y1="450" x2="500" y2="550" stroke="white" strokeWidth="2" opacity="0.7" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '1.5s' }} />
            <line x1="600" y1="450" x2="800" y2="450" stroke="white" strokeWidth="2" opacity="0.7" strokeDasharray="5,5" className="animate-dash" style={{ animationDelay: '0.8s' }} />
            
            {/* Secondary connections */}
            <line x1="750" y1="300" x2="700" y2="600" stroke="white" strokeWidth="1" opacity="0.5" strokeDasharray="3,3" />
            <line x1="450" y1="400" x2="500" y2="550" stroke="white" strokeWidth="1" opacity="0.5" strokeDasharray="3,3" />
            <line x1="650" y1="350" x2="720" y2="480" stroke="white" strokeWidth="1" opacity="0.5" strokeDasharray="3,3" />
          </g>
          
          {/* Large wireframe diamond/pyramid structure */}
          <g className="animate-pulse" style={{ animationDuration: '4.5s' }}>
            {/* 3D Diamond structure */}
            <path d="M 600 650 L 520 730 L 680 730 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8" />
            <path d="M 600 650 L 520 730 L 600 810 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.7" />
            <path d="M 600 650 L 680 730 L 600 810 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.7" />
            <path d="M 520 730 L 600 810 L 680 730 Z" fill="none" stroke="white" strokeWidth="2.5" opacity="0.8" />
            
            {/* Inner diamond star pattern */}
            <path d="M 600 710 L 560 750 L 600 790 L 640 750 Z" fill="none" stroke="white" strokeWidth="2" opacity="0.9">
              <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
            </path>
            
            {/* Grid lines inside diamond */}
            <line x1="580" y1="740" x2="620" y2="740" stroke="white" strokeWidth="0.5" opacity="0.6" />
            <line x1="570" y1="760" x2="630" y2="760" stroke="white" strokeWidth="0.5" opacity="0.6" />
            <line x1="580" y1="780" x2="620" y2="780" stroke="white" strokeWidth="0.5" opacity="0.6" />
          </g>
          
          {/* Mesh network cluster - left side */}
          <g className="animate-pulse" style={{ animationDuration: '3.8s' }}>
            <circle cx="300" cy="600" r="6" fill="white" opacity="0.8" />
            <circle cx="240" cy="640" r="6" fill="white" opacity="0.8" />
            <circle cx="270" cy="700" r="6" fill="white" opacity="0.8" />
            <circle cx="350" cy="660" r="6" fill="white" opacity="0.8" />
            <circle cx="310" cy="680" r="6" fill="white" opacity="0.8" />
            <circle cx="280" cy="640" r="5" fill="white" opacity="0.7" />
            
            {/* Mesh connections */}
            <line x1="300" y1="600" x2="240" y2="640" stroke="white" strokeWidth="1" opacity="0.6" />
            <line x1="300" y1="600" x2="270" y2="700" stroke="white" strokeWidth="1" opacity="0.6" />
            <line x1="300" y1="600" x2="350" y2="660" stroke="white" strokeWidth="1" opacity="0.6" />
            <line x1="300" y1="600" x2="280" y2="640" stroke="white" strokeWidth="1" opacity="0.6" />
            <line x1="240" y1="640" x2="270" y2="700" stroke="white" strokeWidth="1" opacity="0.5" />
            <line x1="350" y1="660" x2="310" y2="680" stroke="white" strokeWidth="1" opacity="0.5" />
            <line x1="270" y1="700" x2="310" y2="680" stroke="white" strokeWidth="1" opacity="0.5" />
            <line x1="280" y1="640" x2="240" y2="640" stroke="white" strokeWidth="1" opacity="0.5" />
          </g>
          
          {/* Data flow particles - animated */}
          <g>
            <circle cx="650" cy="250" r="3" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2s' }} />
            <circle cx="720" cy="380" r="3" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2.3s', animationDelay: '0.5s' }} />
            <circle cx="580" cy="350" r="3" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2.1s', animationDelay: '0.3s' }} />
            <circle cx="680" cy="520" r="3" fill="white" opacity="0.9" className="animate-ping" style={{ animationDuration: '2.4s', animationDelay: '0.7s' }} />
          </g>
          
          {/* Rotating scan arc - large */}
          <path 
            d="M 600 100 A 350 350 0 0 1 950 450" 
            fill="none" 
            stroke="white" 
            strokeWidth="3" 
            strokeLinecap="round"
            opacity="0.7"
            className="animate-rotate-scan"
          />
          
          {/* Additional rotating arc */}
          <path 
            d="M 600 200 A 250 250 0 0 0 350 450" 
            fill="none" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round"
            opacity="0.6"
            className="animate-rotate-scan"
            style={{ animationDirection: 'reverse' }}
          />
          
          {/* Grid lines radiating from center */}
          <g opacity="0.4">
            <line x1="600" y1="450" x2="900" y2="450" stroke="white" strokeWidth="1" strokeDasharray="8,8" />
            <line x1="600" y1="450" x2="800" y2="250" stroke="white" strokeWidth="1" strokeDasharray="8,8" />
            <line x1="600" y1="450" x2="800" y2="650" stroke="white" strokeWidth="1" strokeDasharray="8,8" />
            <line x1="600" y1="450" x2="700" y2="200" stroke="white" strokeWidth="1" strokeDasharray="8,8" />
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 pt-32 md:pt-40">
        <div className="max-w-5xl">
          <p
            className="animate-hero-rise inline-block text-xs md:text-sm uppercase tracking-[0.3em] text-black/85 font-medium mb-8 md:mb-12 border border-black/40 rounded-full px-4 py-1.5"
            style={{ animationDelay: '0ms' }}
          >
            A Sovereign RegTech &amp; AI Solutions Company
          </p>

          <h1
            className="animate-hero-rise text-3xl md:text-5xl lg:text-7xl font-bold text-black leading-[1.15] tracking-tight mb-10 md:mb-16 max-w-5xl"
            style={{ animationDelay: '120ms' }}
          >
            Bridging the Gap Between
            <br />
            High-Level Policy and
            <br />
            Functional Execution
          </h1>

          <p
            className="animate-hero-rise text-xl md:text-2xl lg:text-3xl text-black/85 font-light tracking-wide leading-snug max-w-3xl"
            style={{ animationDelay: '240ms' }}
          >
            Engineering the future of African digital
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            governance and industrial sovereignty.
          </p>

          {/* CTA Group */}
          <div
            className="animate-hero-rise mt-10 md:mt-14 flex flex-col sm:flex-row gap-4"
            style={{ animationDelay: '360ms' }}
          >
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-engage-btn"
              className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-black text-white font-semibold rounded-full hover:bg-[#2d2d2d] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 focus:ring-offset-[#3eb5a0]"
            >
              <span>Engage Reshadi</span>
              <span className="w-6 h-6 rounded-full bg-[#3eb5a0] text-black flex items-center justify-center text-sm font-bold transition-transform duration-300 group-hover:translate-x-1">
                &rarr;
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToServices}
        className="absolute bottom-6 md:bottom-8 left-6 md:left-12 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-black bg-black text-white flex items-center justify-center hover:bg-black/80 transition-all duration-300 group"
        aria-label="Scroll to services"
      >
        <ChevronDown size={20} className="md:hidden text-white" />
        <ChevronDown size={24} className="hidden md:block text-white" />
      </button>
    </section>
  );
};

export default Hero;